import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import Tooltip from '@ublend-npm/react-tooltip';
import Menu from '@ublend-npm/aulaui-next-web/Menu';
import MenuItem from '@ublend-npm/aulaui-next-web/MenuItem';

import Separator from './Separator';
import Filter from './Filter';
import ParticipantsList from './ParticipantsList.container';
import AddUserDialog from './ParticipantsTab/AddUserDialog';
import ParticipantHistoryDialog from './ParticipantsTab/ParticipantHistoryDialog';
import {
  Container,
  Wrapper,
  TopRow,
  AddButton,
  AddIcon,
  IconsContainer,
  IconButton,
  HistoryIcon,
  CloudDownloadIcon,
  ParticipantsCount,
} from './ParticipantsTab.styled';
import SortParticipants from './SortParticipants/SortParticipants.container';

const DownloadItems = [
  {
    key: 'all',
    label: 'Download a CSV with all participants',
  },
  {
    key: 'student',
    label: 'Download a CSV with all students',
  },
  {
    key: 'educator',
    label: 'Download a CSV with all educators',
  },
];

const ParticipantsTab = ({
  searchParticipants,
  exportParticipants,
  participantFilter,
  isEducator,
  participantsCount,
  filteredParticipantsCount,
  accessingAsStaff,
}) => {
  const [filter, setFilter] = useState();
  const [openAddUserDialog, setOpenAddUserDialog] = useState(false);
  const [
    openParticipantHistoryDialog,
    setOpenParticipantHistoryDialog,
  ] = useState(false);
  const [sortBy, setSortBy] = useState('lastName');
  useEffect(() => {
    if (participantFilter && filter === undefined) {
      setFilter(participantFilter);
    }
  });

  const search = useCallback(debounce(searchParticipants, 200), []);

  const changeSortType = (sortType) => {
    setSortBy(sortType);
  };

  const onFilterChange = (query) => {
    setFilter(query);
    search(query);
  };

  const [downloadingParticipants, setDownloadingParticipants] = useState(false);
  const onDownloadSelect = async ({ item }) => {
    setDownloadingParticipants(true);
    await exportParticipants({
      role: item !== 'all' ? item : undefined,
      sort: sortBy,
    });
    setDownloadingParticipants(false);
  };

  const addEducatorText = 'Add educators';
  const viewHistoryText = 'View participants history';
  const downloadParticipantsText = 'Download a CSV file with the participants';
  const participantsNoun =
    participantsCount > 1 || participantsCount === 0
      ? 'participants'
      : 'participant';

  const displayActionIcons = () => (
    <IconsContainer>
      <Menu
        ariaLabelledby="download-participants-csv"
        renderTarget={({ handleTargetClick }) => (
          <Tooltip title={downloadParticipantsText} position="top">
            <IconButton
              icon={CloudDownloadIcon}
              size="small"
              label={downloadParticipantsText}
              onClick={handleTargetClick}
              loading={downloadingParticipants}
              disabled={downloadingParticipants}
            />
          </Tooltip>
        )}
        onSelect={onDownloadSelect}
        renderChildren={({ getItemProps }) =>
          DownloadItems.map(({ key, label }) => (
            <MenuItem
              key={`action:${key}`}
              size="medium"
              primaryText={label}
              {...getItemProps({
                item: key,
                onClick: (e) => e.stopPropagation(),
              })}
            />
          ))
        }
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
      />
      <Tooltip title={viewHistoryText} position="top">
        <IconButton
          icon={HistoryIcon}
          size="small"
          label={viewHistoryText}
          onClick={() =>
            setOpenParticipantHistoryDialog(!openParticipantHistoryDialog)
          }
        />
      </Tooltip>
      {isEducator && (
        <Tooltip title={addEducatorText} position="top">
          <AddButton
            icon={AddIcon}
            size="small"
            label={addEducatorText}
            onClick={() => setOpenAddUserDialog(!openAddUserDialog)}
          />
        </Tooltip>
      )}
    </IconsContainer>
  );

  return (
    <React.Fragment>
      <Container>
        <TopRow>
          <Filter
            title="Search for participants"
            onChange={onFilterChange}
            filter={filter}
          />
          {isEducator || accessingAsStaff ? displayActionIcons() : null}
        </TopRow>
        <Separator height="40px" />
        <TopRow>
          <ParticipantsCount>{`${
            filter ? filteredParticipantsCount : participantsCount
          } ${participantsNoun}`}</ParticipantsCount>
          <SortParticipants sortBy={sortBy} changeSortType={changeSortType} />
        </TopRow>
        <Wrapper aria-live="polite">
          <ParticipantsList filter={filter} sortBy={sortBy} />
        </Wrapper>
      </Container>
      {isEducator || accessingAsStaff ? (
        <>
          {isEducator && (
            <AddUserDialog
              open={openAddUserDialog}
              onClose={() => setOpenAddUserDialog(false)}
            />
          )}
          <ParticipantHistoryDialog
            open={openParticipantHistoryDialog}
            onClose={() => setOpenParticipantHistoryDialog(false)}
          />
        </>
      ) : null}
    </React.Fragment>
  );
};

ParticipantsTab.propTypes = {
  searchParticipants: PropTypes.func.isRequired,
  exportParticipants: PropTypes.func.isRequired,
  participantFilter: PropTypes.string,
  isEducator: PropTypes.bool.isRequired,
  participantsCount: PropTypes.number.isRequired,
  filteredParticipantsCount: PropTypes.number.isRequired,
  accessingAsStaff: PropTypes.bool.isRequired,
};

ParticipantsTab.defaultProps = {
  participantFilter: '',
};

export default React.memo(ParticipantsTab);
